import React from 'react';
import { getIntl } from 'umi';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import { measureTextWidth } from '@intelligence/v3/utils/bom';
import { valueFormat, growthFormat } from '@intelligence/utils/commonUtils';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';
import { SortMode, SortType } from '@home/enums/common';

import type { BubbleProps, TableColumn } from 'tea-component';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';

// 基础配置
export interface IColumnBaseOption {
  // 当前语言环境、默认为英文
  locale?: string;
  // 列宽（中文环境下）
  widthCN?: number | string;
  // 指标气泡Bubble的placement属性
  bubblePlacement?: BubbleProps['placement'];
}

// Header信息
export interface IColumnHeaderOption {
  // 是否自动处理国际化（Header字段传的国际化配置的key）
  headerTranslatable?: boolean;
  // Header字段传的国际化配置的key
  headerTranslateKey?: string;
  // Header内容（不自动处理国际化）
  header?: string | React.ReactElement;
}

/**
 * 判断指标列宽是否需要为当前环比排序图标预留空间
 */
export const getHasRatioIcon = <T = IRecord,>(
  dataSource: string,
  metric: string,
  sorts: IRatioMetricSortState<T>[] | undefined,
): boolean => {
  return (sorts || []).some((item) => {
    return (
      item?.dataSource === dataSource &&
      item?.metric === metric &&
      item?.type !== SortType.None &&
      item?.mode === SortMode.Ratio
    );
  });
};

// 与 ColumnsCenter 内部 TABLE_PADDING 保持一致
const METRICS_TABLE_COLUMN_PADDING = 40;

/**
 * @description 指标列宽计算策略：
 * 1. 先按表头一行文本宽度精确测量（700 14px）；
 * 2. 再叠加头部附加元素占位（数据源 icon / 排序 icon / 提示 icon）；
 * 3. 最后加上表格左右 padding，并做兜底最小宽度，避免列被压缩。
 * @param params.title 列标题文本
 * @param params.dataSource 列标题所属数据源（用于判断是否有数据源 icon）
 * @param params.hasSortIcon 是否有排序功能（用于判断是否有排序 icon）
 * @param params.hasRatioIcon 是否有环比功能（用于判断是否有环比 icon）
 * @param params.hasTipText 是否有提示文本（用于判断是否有提示 icon）
 */
export const calcMetricColumnWidth = (params: {
  title: string;
  dataSource?: string;
  hasSortIcon?: boolean;
  hasRatioIcon?: boolean;
  hasTipText?: boolean;
}) => {
  const dataSource = params?.dataSource || '';
  const title = params?.title || '';
  const hasSortIcon = params?.hasSortIcon || false;
  const hasRatioIcon = params?.hasRatioIcon || false;
  const hasTipText = params?.hasTipText || false;

  // 文本宽度：与 SortBy 标题字体保持一致（font-weight: 700; font-size: 14px）
  const titleWidth = !title ? 0 : measureTextWidth(title, '700 14px Arial');

  // 头部附加元素占位（经验值，按 SortBy 结构估算）
  // 数据源 icon：20px + 右边距 4px
  const hasSourceIcon = Boolean(getDataSourceIcon(dataSource));
  const sourceIconWidth = hasSourceIcon ? 24 : 0;
  // 环比排序时，会有一个环比 icon，约 26px
  const ratioIconWidth = hasRatioIcon ? 26 : 0;
  // 排序 icon：16px + 左边距 6px
  const sortIconWidth = hasSortIcon ? 22 : 0;
  // 提示 icon：约 16px + 左边距 6px
  const tipIconWidth = hasTipText ? 22 : 0;

  // 内容宽度（文本 + 附加元素）
  const contentWidth = Math.ceil(titleWidth + sourceIconWidth + sortIconWidth + ratioIconWidth + tipIconWidth);
  // 内容兜底：标题为空或极短时，至少容纳一个基础 icon 区域
  const minContentWidth = 24 + 22;

  // 最终宽度 = max(内容宽度, 内容兜底) + 表格 padding，并限制最小 200
  return Math.max(Math.max(contentWidth, minContentWidth) + METRICS_TABLE_COLUMN_PADDING, 200);
};

export const getHeaderWidthIntelligence = (
  // 当前语言环境
  locale: string,
  // 其他信息
  props: {
    // 宽度（英文）
    width: number | string;
    // 宽度（中文）
    widthCN?: number | string;
  },
) => {
  const { width, widthCN } = props || {};
  const widthMap: IRecord<number | string> = {
    [LANGUAGE_EN]: width,
    [LANGUAGE_CN]: widthCN ?? width,
  };
  return widthMap[locale] ?? width;
};

// 获取Header
export const getHeaderIntelligence = (
  // 当前语言环境
  locale: string,
  // Header信息
  props?: IColumnHeaderOption,
): string | React.ReactElement => {
  const { headerTranslatable = false, headerTranslateKey = '', header = '' } = props || {};
  if (!headerTranslatable) return header;
  const { formatMessage } = getIntl(locale);
  return formatMessage({ id: headerTranslateKey });
};

// 排序信息（简单排序，仅能支持单字段的数值排序）
export interface IColumnSimpleSortOption {
  // 是否支持排序
  sortable?: boolean;
  // 表格当前排序字段的数据源（针对指标，指标有归属数据源概念）
  sortDataSource?: string;
  // 表格当前排序字段
  sortMetric?: string;
  // 表格当前排序字段的排序方式（升序、降序）
  sortValue?: SortType;
  // 排序的回调方法
  onSortChange?: (payload?: SortByItemType) => void;
}

/**
 * 获取列header
 * @param props 配置
 * @param props.key 列的key
 * @param props.header 列的header
 * @param props.align 列的水平对齐方式
 * @param props.sortable 列是否支持排序
 * @param props.sortMetric 表格当前排序字段
 * @param props.sortValue 表格当前排序字段的排序方式（升序、降序）
 * @param props.onSortChange 日期列排序的回调方法
 */
export const getSimpleSortableHeaderIntelligence = (
  props: IColumnSimpleSortOption & Pick<TableColumn, 'key' | 'header' | 'align'>,
): string | React.ReactElement => {
  const {
    key,
    header,
    align = 'left',
    sortable = false,
    sortDataSource = '',
    sortMetric = '',
    sortValue = SortType.None,
    onSortChange,
  } = props || {};
  if (!sortable) return header as any;

  return (
    <SortBy
      align={align}
      title={header}
      defaultKey={sortMetric}
      defaultValue={sortValue}
      svgIcon={getDataSourceIcon(sortDataSource)}
      list={[
        {
          key,
          sortMetric: key,
          text: header,
          title: header,
          ratioType: 0,
        },
      ]}
      onChange={onSortChange}
    />
  );
};

// KMB指标的默认格式方法，数值转换为KMB格式，并保留两位小数
export const defaultKMBMetricValueFormatter = (value: string | number | null): string | number => {
  return valueFormat(value, false, 0);
};

// 环比配置
export interface IRatioValueConfigs {
  // 环比取值字段
  key?: string;
  valueType?: 'pointValue' | 'percentValue' | 'rankValue' | ({} & string);
  hasPoint?: 'none' | 'all' | 'lessThanOne' | ({} & string);
  // 小数位数
  fixedNum?: number;
  // 最大百分数限制
  maxNumber?: number;
}

export const DEFAULT_RATIO_VALUE_CONFIGS = {
  VALUE_TYPE: 'pointValue',
  HAS_POINT: 'all',
  FIXED_NUMBER: 2,
  MAX_NUMBER: 99999,
};

export const defaultKMBMetricRatioValueFormatter = (
  value: string | number | null,
  configs?: IRatioValueConfigs | null,
) => {
  const valueType = configs?.valueType ?? DEFAULT_RATIO_VALUE_CONFIGS.VALUE_TYPE;
  const hasPoint = configs?.fixedNum ?? DEFAULT_RATIO_VALUE_CONFIGS.HAS_POINT;
  const fixedNum = configs?.fixedNum ?? DEFAULT_RATIO_VALUE_CONFIGS.FIXED_NUMBER;
  const maxNumber = configs?.maxNumber ?? DEFAULT_RATIO_VALUE_CONFIGS.MAX_NUMBER;

  return growthFormat({
    value,
    valueType,
    hasPoint,
    fixedNum,
    maxNumber,
  });
};
